import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

import { css, keyframes } from '@compiled/react';
import { useIntl } from 'react-intl';

import { media } from '@atlaskit/primitives/responsive';
import { token } from '@atlaskit/tokens';

import { jsmPortalPlaygroundMessages as messages } from '../jsm-portal-playground-messages';

type GuideFit = 'measuring' | 'fits' | 'cut-off';

const CLIPPING_OVERFLOW = ['auto', 'clip', 'hidden', 'scroll'];

const isGuideCutOff = (wrapper: HTMLElement) => {
	const bounds = {
		left: 0,
		right: document.documentElement.clientWidth,
		top: -Infinity,
		bottom: Infinity,
	};
	for (let ancestor = wrapper.parentElement; ancestor; ancestor = ancestor.parentElement) {
		const { overflowX, overflowY } = window.getComputedStyle(ancestor);
		const clipsX = CLIPPING_OVERFLOW.includes(overflowX);
		const clipsY = CLIPPING_OVERFLOW.includes(overflowY);
		if (!clipsX && !clipsY) {
			continue;
		}
		const rect = ancestor.getBoundingClientRect();
		if (clipsX) {
			bounds.left = Math.max(bounds.left, rect.left);
			bounds.right = Math.min(bounds.right, rect.right);
		}
		if (clipsY) {
			bounds.top = Math.max(bounds.top, rect.top);
			bounds.bottom = Math.min(bounds.bottom, rect.bottom);
		}
	}

	return Array.from(wrapper.querySelectorAll('svg, span')).some((part) => {
		const rect = part.getBoundingClientRect();
		return (
			rect.left < bounds.left ||
			rect.right > bounds.right ||
			rect.top < bounds.top ||
			rect.bottom > bounds.bottom
		);
	});
};

export const JsmPlaygroundSeeHowItWorks = () => {
	const intl = useIntl();
	const wrapperRef = useRef<HTMLDivElement>(null);
	const measuredScaleRef = useRef<string | null>();
	const [fit, setFit] = useState<GuideFit>('measuring');

	const measure = useCallback(() => {
		const wrapper = wrapperRef.current;
		if (wrapper) {
			setFit(isGuideCutOff(wrapper) ? 'cut-off' : 'fits');
		}
	}, []);

	useLayoutEffect(() => {
		const scale =
			wrapperRef.current?.closest('[data-preview-scale]')?.getAttribute('data-preview-scale') ??
			null;
		if (scale !== measuredScaleRef.current) {
			measuredScaleRef.current = scale;
			measure();
		}
	});

	useEffect(() => {
		window.addEventListener('resize', measure);
		return () => window.removeEventListener('resize', measure);
	}, [measure]);

	const isHidden = fit !== 'fits';

	return (
		<div
			ref={wrapperRef}
			css={[guideWrapperStyles, isHidden && hiddenStyles]}
			data-testid="jsm-playground-see-how-it-works"
			aria-hidden="true"
		>
			<div css={guideStyles}>
				<svg
					css={[guideArrowStyles, isHidden && staticStyles]}
					width="60"
					height="80"
					viewBox="0 0 60 80"
					fill="none"
				>
					<path
						d="M48 6 C44 6, 28 10, 18 26 C10 40, 10 62, 12 74"
						stroke="currentColor"
						strokeWidth="1.8"
						strokeLinecap="round"
					/>
					<path
						d="M6 68 L12 74 L18 70"
						stroke="currentColor"
						strokeWidth="1.8"
						strokeLinecap="round"
						strokeLinejoin="round"
					/>
				</svg>
				<span css={[guideTextStyles, guideTextMotionStyles, isHidden && staticStyles]}>
					{intl.formatMessage(messages.seeHowItWorks)}
				</span>
			</div>
		</div>
	);
};

const guideWrapperStyles = css({
	position: 'absolute',
	insetBlockStart: 0,
	insetInlineEnd: '80px',
	zIndex: 10,
	transform: 'translate(230px, 100px)',
	pointerEvents: 'none',
	[media.below.md]: {
		display: 'none',
	},
});

const hiddenStyles = css({
	visibility: 'hidden',
});

const staticStyles = css({
	animation: 'none',
});

const guideStyles = css({
	display: 'flex',
	alignItems: 'flex-start',
	gap: token('space.050'),
	paddingBlockStart: token('space.200'),
	color: token('color.text'),
});

const arrowIn = keyframes({
	from: {
		opacity: 0,
		transform: 'translateY(-150px) rotate(60deg) scale(0.4)',
	},
	to: {
		opacity: 1,
		transform: 'translateY(-150px) rotate(60deg) scale(1)',
	},
});

const textIn = keyframes({
	from: {
		opacity: 0,
		transform: 'translate(4px, -107px) translateY(6px)',
	},
	to: {
		opacity: 1,
		transform: 'translate(4px, -107px) translateY(0)',
	},
});

const guideArrowStyles = css({
	flexShrink: 0,
	transform: 'translateY(-150px) rotate(60deg)',
	animation: `${arrowIn} 420ms cubic-bezier(0.33, 0, 0.2, 1) both`,
	'@media (prefers-reduced-motion: reduce)': {
		animation: 'none',
	},
});

const guideTextStyles = css({
	margin: 0,
	fontFamily:
		'"Atlassian Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
	fontSize: '14px',
	fontWeight: token('font.weight.bold'),
	lineHeight: '20px',
	maxWidth: '80px',
});

const guideTextMotionStyles = css({
	transform: 'translate(4px, -107px)',
	animation: `${textIn} 280ms cubic-bezier(0.33, 0, 0.2, 1) 320ms both`,
	'@media (prefers-reduced-motion: reduce)': {
		animation: 'none',
	},
});
